import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, tap } from 'rxjs';
import {
  PupilAPIActions,
  PupilContactDialogActions,
  PupilCoursesDialogActions,
  PupilDetailsInformationActions,
  PupilProfileDialogActions,
  PupilSchoolDialogActions,
} from './pupil.actions';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  ContactForm,
  ProfileForm,
  SchoolForm,
} from '@lyceo/homi/pupils/util-models';
import {
  PUPIL_CONTACT_MODAL,
  PUPIL_COURSES_MODAL,
  PUPIL_PROFILE_MODAL,
  PUPIL_SCHOOL_MODAL,
} from '@lyceo/homi/pupils/util';
import { ComponentType } from '@angular/cdk/portal';
import { PupilFacade } from './pupil.facade';
import { PupilService } from '../../pupil.service';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class PupilInformationEffects {
  private readonly actions$: Actions = inject(Actions);
  private readonly dialog: Dialog = inject(Dialog);
  private readonly pupilFacade: PupilFacade = inject(PupilFacade);
  private readonly pupilService: PupilService = inject(PupilService);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly translocoService: TranslocoService =
    inject(TranslocoService);
  private readonly pupilProfileModal: ComponentType<unknown> =
    inject<ComponentType<unknown>>(PUPIL_PROFILE_MODAL);
  private readonly pupilContactModal: ComponentType<unknown> =
    inject<ComponentType<unknown>>(PUPIL_CONTACT_MODAL);
  private readonly pupilSchoolModal: ComponentType<unknown> =
    inject<ComponentType<unknown>>(PUPIL_SCHOOL_MODAL);
  private readonly pupilCoursesModal: ComponentType<unknown> =
    inject<ComponentType<unknown>>(PUPIL_COURSES_MODAL);

  public openEditProfileDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilDetailsInformationActions.editProfileClicked),
      exhaustMap(() => {
        const dialogRef: DialogRef<ProfileForm> = this.dialog.open(
          this.pupilProfileModal,
          {
            id: 'profile-dialog',
          },
        );

        return dialogRef.closed.pipe(
          map(() => PupilProfileDialogActions.closed()),
        );
      }),
    );
  });

  public submitProfile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilProfileDialogActions.submitProfile),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.pupilFacade.paramPupilId();
        if (!pupilId) {
          return of(
            PupilAPIActions.patchPupilProfileFailure({
              error: new Error('Pupil ID is undefined'),
            }),
          );
        }

        return this.pupilService
          .patchPupilProfile(pupilId, action.profile)
          .pipe(
            map((pupil) =>
              PupilAPIActions.patchPupilProfileSuccess({
                pupil: pupil,
              }),
            ),
            catchError((error: Error) =>
              of(PupilAPIActions.patchPupilProfileFailure({ error })),
            ),
          );
      }),
    );
  });

  public submitProfileSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PupilAPIActions.patchPupilProfileSuccess),
        tap(() => {
          this.dialog.getDialogById('profile-dialog')?.close();
        }),
      );
    },
    { dispatch: false },
  );

  public openEditContactDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilDetailsInformationActions.editContactClicked),
      exhaustMap(() => {
        const dialogRef: DialogRef<ContactForm> = this.dialog.open(
          this.pupilContactModal,
        );

        return dialogRef.closed.pipe(
          map((dialogResult: ContactForm | undefined) => {
            if (dialogResult) {
              return PupilContactDialogActions.editContact({
                contact: dialogResult,
              });
            }
            return PupilContactDialogActions.closed();
          }),
        );
      }),
    );
  });

  public openEditSchoolDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilDetailsInformationActions.editSchoolClicked),
      exhaustMap(() => {
        const dialogRef: DialogRef<SchoolForm> = this.dialog.open(
          this.pupilSchoolModal,
          {
            panelClass: 'dialog-lg',
          },
        );

        return dialogRef.closed.pipe(
          map((dialogResult: SchoolForm | undefined) => {
            if (dialogResult) {
              return PupilSchoolDialogActions.editSchool({
                school: dialogResult,
              });
            }
            return PupilSchoolDialogActions.closed();
          }),
        );
      }),
    );
  });

  public openEditCoursesDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilDetailsInformationActions.editCoursesClicked),
      exhaustMap(() => {
        const dialogRef: DialogRef = this.dialog.open(this.pupilCoursesModal, {
          id: 'edit-courses-dialog',
          panelClass: 'dialog-lg',
        });

        return dialogRef.closed.pipe(
          map(() => PupilCoursesDialogActions.closed()),
        );
      }),
    );
  });

  public updatePupilCourses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PupilCoursesDialogActions.submitCourses),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.pupilFacade.paramPupilId();
        if (!pupilId) {
          return of(
            PupilAPIActions.updatePupilCoursesFailure({
              error: new Error('Pupil ID is undefined'),
            }),
          );
        }

        return this.pupilService
          .updatePupilCourses(pupilId, { courseIds: action.courses.courseIds })
          .pipe(
            map(() =>
              PupilAPIActions.updatePupilCoursesSuccess({
                pupilId,
                courseIds: action.courses.courseIds,
              }),
            ),
            catchError((error: Error) =>
              of(PupilAPIActions.updatePupilCoursesFailure({ error })),
            ),
          );
      }),
    );
  });

  public updatePupilCoursesSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(PupilAPIActions.updatePupilCoursesSuccess),
        tap(() => {
          this.dialog.getDialogById('edit-courses-dialog')?.close();

          this.applicationFacade.showToaster({
            type: 'success',
            message: this.translocoService.translate(
              'toasts.course_update_success',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );
}
